const React = require('react')
const { useState } = require('react')
const { useParams, useHistory } = require('react-router-dom')
const { UncontrolledTooltip, Table } = require('reactstrap')
const { useNetworkConfig } = require('@contexts')
const { useFormatBytes, useFormatTimestampShort, useFormatDateTime, useResponsive, BREAKPOINTS } = require('@hooks')
const { LoadingSpinner, CopyButton } = require('@components')
const { TableHeader } = require('@components/table')
const { TransactionsPopover } = require('@components/popover/transaction')

require('@styles/pages/address/transactions.address.css')

const AddressTokenTransfers = props => {
    const { loading, data } = props
    const { address } = useParams()
    const history = useHistory()
    const { network } = useNetworkConfig()
    const formatBytes = useFormatBytes()
    const formatTimestamp = useFormatTimestampShort()
    const formatDateTime = useFormatDateTime()
    const [showAge, setShowAge] = useState(true)
    const isMobile = useResponsive(BREAKPOINTS.extraLarge)

    const [activePreview, setActivePreview] = useState(null)

    return (
        <div className='box table-section'>
            {loading ? (
                <div className='d-flex justify-content-center align-items-center' style={{ height: '300px' }}>
                    <LoadingSpinner />
                </div>
            ) : (
                <>
                    {data?.tokenTransfers.totalItems > 0 && (
                        <TableHeader
                            title={
                                <>
                                    Latest&nbsp;
                                    {data?.tokenTransfers.totalItems > 25 ? '25' : data?.tokenTransfers.totalItems}{' '}
                                    Token Transfer Event
                                    {data?.tokenTransfers.totalItems > 1 ? 's' : ''}&nbsp;
                                    {data?.tokenTransfers.totalItems > 25 && (
                                        <>
                                            (
                                            <span
                                                className={`${network.name}-text-color`}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => history.push(`/${network.name}/tokentxns?a=${address}`)}
                                            >
                                                View All
                                            </span>
                                            )
                                        </>
                                    )}
                                </>
                            }
                            usePagination={false}
                        />
                    )}
                    <Table
                        responsive
                        style={isMobile ? { display: 'block', overflowX: 'auto', whiteSpace: 'nowrap' } : {}}
                    >
                        <thead>
                            <tr>
                                <th>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}
                                    >
                                        <div
                                            id='preview-tooltip'
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <i
                                                className='fas fa-circle-question'
                                                style={{
                                                    fontSize: '14px',
                                                    color: 'var(--grey-color)'
                                                }}
                                            />
                                        </div>
                                        <UncontrolledTooltip placement='top' target='preview-tooltip'>
                                            See preview of transactions details.
                                        </UncontrolledTooltip>
                                    </div>
                                </th>
                                <th>Transaction Hash</th>
                                <th>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}
                                    >
                                        <span>Method</span>
                                        <div
                                            id='method-tooltip'
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <i
                                                className='fas fa-circle-question'
                                                style={{
                                                    fontSize: '14px',
                                                    color: 'var(--grey-color)'
                                                }}
                                            />
                                        </div>
                                        <UncontrolledTooltip placement='top' target='method-tooltip'>
                                            Function executed based on decoded input data. For unidentified functions,
                                            method ID is displayed instead
                                        </UncontrolledTooltip>
                                    </div>
                                </th>
                                <th>Block</th>
                                <th>
                                    <span
                                        id='time-format-tooltip'
                                        className={`${network.name}-text-color cursor-pointer`}
                                        onClick={() => setShowAge(!showAge)}
                                    >
                                        {showAge ? 'Age' : 'Date Time (UTC)'}
                                    </span>
                                    <UncontrolledTooltip placement='top' target='time-format-tooltip'>
                                        {showAge ? 'Click to show Datetime Format' : 'Click to show Age Format'}
                                    </UncontrolledTooltip>
                                </th>
                                <th>From</th>
                                <th></th>
                                <th>To</th>
                                <th>Amount</th>
                                <th>Token</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data?.tokenTransfers.items.map((transaction, index) => {
                                return (
                                    <tr key={transaction.hash}>
                                        <td>
                                            <TransactionsPopover
                                                id={`preview-${transaction.hash}-${index}`}
                                                transaction={transaction}
                                                activePreview={activePreview}
                                                setActivePreview={setActivePreview}
                                            />
                                        </td>
                                        <td>
                                            {!transaction.status && (
                                                <>
                                                    <div
                                                        className='tx-status-indicator'
                                                        style={{ color: 'var(--fail-color)' }}
                                                        id={`error-${transaction.hash}`}
                                                    >
                                                        <i className='fas fa-times' />
                                                    </div>
                                                    <UncontrolledTooltip
                                                        placement='top'
                                                        target={`error-${transaction.hash}`}
                                                    >
                                                        Error in Txn: execution reverted
                                                    </UncontrolledTooltip>
                                                </>
                                            )}
                                            <span
                                                id={`parent-tx-hash-${transaction.hash}`}
                                                className={`${network.name}-text-color cursor-pointer`}
                                                onClick={() => history.push(`/${network.name}/tx/${transaction.hash}`)}
                                            >
                                                {formatBytes(transaction.hash, 12, 9, 0)}
                                            </span>
                                            <UncontrolledTooltip
                                                placement='top'
                                                target={`parent-tx-hash-${transaction.hash}`}
                                            >
                                                {transaction.hash}
                                            </UncontrolledTooltip>
                                            &nbsp;
                                            <CopyButton
                                                id={`copy-parent-tx-hash-${transaction.hash}`}
                                                data={transaction.hash}
                                                text={'Copy Transaction Hash'}
                                            />
                                        </td>
                                        <td>
                                            <span id={`method-id-${transaction.hash}`} className='method-id'>
                                                {transaction.functionName}
                                            </span>
                                            <UncontrolledTooltip
                                                placement='top'
                                                target={`method-id-${transaction.hash}`}
                                            >
                                                {transaction.functionName}
                                            </UncontrolledTooltip>
                                        </td>
                                        <td>
                                            <span
                                                className={`${network.name}-text-color cursor-pointer`}
                                                onClick={() =>
                                                    history.push(`/${network.name}/block/${transaction.blockNumber}`)
                                                }
                                            >
                                                {transaction.blockNumber}
                                            </span>
                                        </td>
                                        <td>
                                            {showAge ? (
                                                <>
                                                    <span id={'age-' + transaction.hash}>
                                                        {formatTimestamp(transaction.blockTimestamp)}
                                                    </span>
                                                    <UncontrolledTooltip
                                                        placement='top'
                                                        target={`age-${transaction.hash}`}
                                                    >
                                                        {formatDateTime(transaction.blockTimestamp)}
                                                    </UncontrolledTooltip>
                                                </>
                                            ) : (
                                                <>
                                                    <span id={'datetime-' + transaction.blockNumber}>
                                                        {formatDateTime(transaction.blockTimestamp)}
                                                    </span>
                                                    <UncontrolledTooltip
                                                        placement='top'
                                                        target={`datetime-${transaction.blockNumber}`}
                                                    >
                                                        {formatTimestamp(transaction.blockTimestamp)}
                                                    </UncontrolledTooltip>
                                                </>
                                            )}
                                        </td>
                                        <td>
                                            {transaction.transferFrom.type === 'SCA' && (
                                                <>
                                                    <i
                                                        className='fas fa-file-alt'
                                                        style={{
                                                            fontSize: '14px',
                                                            color: 'var(--grey-color)'
                                                        }}
                                                    />
                                                    &nbsp;
                                                </>
                                            )}
                                            <span
                                                id={`from-${transaction.hash}`}
                                                className={
                                                    transaction.transferFrom.address !== address &&
                                                    `${network.name}-text-color cursor-pointer`
                                                }
                                                onClick={() =>
                                                    transaction.transferFrom.address !== address &&
                                                    history.push(
                                                        `/${network.name}/address/${transaction.transferFrom.address}`
                                                    )
                                                }
                                            >
                                                {formatBytes(transaction.transferFrom.address, 20, 7, 6)}
                                            </span>
                                            <UncontrolledTooltip placement='top' target={`from-${transaction.hash}`}>
                                                {transaction.transferFrom.address}
                                            </UncontrolledTooltip>
                                            &nbsp;
                                            <CopyButton
                                                id={`copy-from-${transaction.hash}`}
                                                data={transaction.transferFrom.address}
                                                text={'Copy Address'}
                                            />
                                        </td>
                                        <td>
                                            {transaction.transferFrom.address === address ||
                                            transaction.transferTo.address === address ? (
                                                <div
                                                    style={{
                                                        padding: '2px 8px',
                                                        display: 'inline-block',
                                                        fontSize: '12px',
                                                        fontWeight: 'bold',
                                                        color:
                                                            transaction.transferFrom.address === address
                                                                ? 'var(--warning-color)'
                                                                : 'var(--success-color)',
                                                        borderRadius: '4px',
                                                        backgroundColor:
                                                            transaction.transferFrom.address === address
                                                                ? 'var(--warning-background-color)'
                                                                : 'var(--success-background-color)'
                                                    }}
                                                >
                                                    {transaction.transferFrom.address === address ? 'OUT' : 'IN'}
                                                </div>
                                            ) : null}
                                        </td>
                                        <td>
                                            {transaction.transferTo.type === 'SCA' && (
                                                <>
                                                    <i
                                                        className='fas fa-file-alt'
                                                        style={{
                                                            fontSize: '14px',
                                                            color: 'var(--grey-color)'
                                                        }}
                                                    />
                                                    &nbsp;
                                                </>
                                            )}
                                            <span
                                                id={`to-${transaction.hash}`}
                                                className={
                                                    transaction.transferTo.address !== address &&
                                                    `${network.name}-text-color cursor-pointer`
                                                }
                                                onClick={() =>
                                                    transaction.transferTo.address !== address &&
                                                    history.push(
                                                        `/${network.name}/address/${transaction.transferTo.address}`
                                                    )
                                                }
                                            >
                                                {formatBytes(transaction.transferTo.address, 20, 7, 6)}
                                            </span>
                                            <UncontrolledTooltip placement='top' target={`to-${transaction.hash}`}>
                                                {transaction.transferTo.address}
                                            </UncontrolledTooltip>
                                            &nbsp;
                                            <CopyButton
                                                id={`copy-to-${transaction.hash}`}
                                                data={transaction.transferTo.address}
                                                text={'Copy Address'}
                                            />
                                        </td>
                                        <td>
                                            {(transaction.tokenValue / 10 ** transaction.tokenDecimals).toLocaleString(
                                                undefined,
                                                {
                                                    minimumFractionDigits: 0,
                                                    maximumFractionDigits: transaction.tokenDecimals
                                                }
                                            )}
                                        </td>
                                        <td>
                                            {transaction.tokenName}&nbsp;
                                            <span style={{ color: 'var(--grey-color)' }}>
                                                ({transaction.tokenSymbol})
                                            </span>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </Table>

                    {data?.tokenTransfers.totalItems > 25 && (
                        <div
                            className={`view-all ${network.name}-text-color-after`}
                            onClick={() => history.push(`/${network.name}/tokentxns?a=${address}`)}
                        >
                            View all erc-20 transfers
                            <i className='fas fa-arrow-right' />
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

module.exports = { AddressTokenTransfers }
